import axios from 'axios';
import { getTokens } from './authApi';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

export interface ShopLandingConfig {
  id?: number;
  businessName: string;
  tagline?: string;
  phone: string;
  email?: string;
  address: {
    street?: string;
    city?: string;
    state?: string;
    zip?: string;
  };
  hours: {
    weekday?: string;
    saturday?: string;
    sunday?: string;
  };
  prePurchasePrice?: number;
  branding: {
    logo?: string;
    heroBackground?: string;
    theme?: string;
    customColors?: any;
  };
  gallery: {
    images?: any[];
    categories?: string[];
  };
  testimonials?: any[];
  social?: {
    facebook?: string;
    instagram?: string;
    google?: string;
  };
}

class ShopLandingConfigApi {
  private getAuthHeaders() {
    const tokens = getTokens();
    if (tokens?.accessToken) {
      return {
        Authorization: `Bearer ${tokens.accessToken}`,
      };
    }
    return {};
  }

  /**
   * Get the shop landing configuration
   */
  async get(): Promise<ShopLandingConfig> {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin/shop/landing-config`, {
        headers: this.getAuthHeaders(),
      });
      return response.data;
    } catch (error: any) {
      console.error('Failed to get shop landing config:', error);
      throw new Error(
        error.response?.data?.message || 'Failed to get shop landing configuration'
      );
    }
  }

  /**
   * Update the shop landing configuration
   */
  async update(config: Partial<ShopLandingConfig>): Promise<ShopLandingConfig> {
    try {
      const response = await axios.put(
        `${API_BASE_URL}/admin/shop/landing-config`,
        config,
        {
          headers: this.getAuthHeaders(),
        }
      );
      return response.data;
    } catch (error: any) {
      console.error('Failed to update shop landing config:', error);
      throw new Error(
        error.response?.data?.message || 'Failed to update shop landing configuration'
      );
    }
  }

  /**
   * Migrate data from localStorage to backend
   */
  async migrateFromLocalStorage(): Promise<boolean> {
    try {
      const localConfig = localStorage.getItem('cnf_shop_config');
      if (!localConfig) {
        console.log('No localStorage config to migrate');
        return false;
      }

      const parsed = JSON.parse(localConfig);

      // Transform from old format to new API format
      const apiConfig: Partial<ShopLandingConfig> = {
        businessName: parsed.name,
        tagline: parsed.tagline,
        phone: parsed.phone,
        email: parsed.email,
        address: {
          street: parsed.address?.street,
          city: parsed.address?.city,
          state: parsed.address?.state,
          zip: parsed.address?.zip,
        },
        hours: {
          weekday: parsed.hours?.weekday,
          saturday: parsed.hours?.saturday,
          sunday: parsed.hours?.sunday,
        },
        prePurchasePrice: parsed.prePurchaseInspection?.startingPrice,
        branding: {
          logo: parsed.branding?.logo,
          heroBackground: parsed.branding?.heroBackground,
          theme: parsed.branding?.theme,
          customColors: parsed.branding?.customColors,
        },
        gallery: {
          images: parsed.gallery?.images || [],
          categories: parsed.gallery?.categories || [],
        },
        testimonials: parsed.testimonials?.reviews || [],
        social: parsed.social,
      };

      await this.update(apiConfig);
      console.log('✅ Successfully migrated localStorage data to backend');

      // Keep localStorage for backward compatibility with landing page
      // (We'll update the landing page to use API in a future iteration)

      return true;
    } catch (error) {
      console.error('Failed to migrate localStorage config:', error);
      return false;
    }
  }
}

export const shopLandingConfigApi = new ShopLandingConfigApi();
